/**
 * StatCard Atom
 * Summary statistic card with icon, value, and trend indicator
 */

import { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/core/components/ui/card';
import { cn } from '@/core/lib/utils';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  trend?: number;
  trendLabel?: string;
  onClick?: () => void;
  isActive?: boolean;
  isLoading?: boolean;
  showPulse?: boolean;
  size?: 'sm' | 'md';
}

export function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColor = 'text-gray-600',
  iconBgColor,
  trend,
  trendLabel,
  onClick,
  isActive = false,
  isLoading = false,
  showPulse = false,
  size = 'md',
}: StatCardProps) {
  const isClickable = !!onClick;
  const horizontal = !!iconBgColor;

  return (
    <Card
      className={cn(
        'transition-all duration-200 h-full flex flex-col',
        isClickable && 'hover:shadow-md',
      )}
      onClick={onClick}
    >
      <CardContent className={cn('p-4 flex-1 flex flex-col justify-between', size === 'sm' && 'p-3')}>
        {isLoading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 w-20 bg-gray-200 rounded" />
            <div className="h-8 w-16 bg-gray-200 rounded" />
            <div className="h-3 w-24 bg-gray-200 rounded" />
          </div>
        ) : horizontal ? (
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {Icon && (
                <div className={cn('p-2 rounded-lg flex-shrink-0', iconBgColor)}>
                  <Icon className={cn('h-5 w-5', iconColor, showPulse && 'animate-pulse', size === 'sm' && 'h-4 w-4')} />
                </div>
              )}
              <div>
                <p className={cn('text-sm text-gray-500', size === 'sm' && 'text-xs')}>{title}</p>
                <p className={cn('text-xl font-semibold text-gray-900 mt-0.5', size === 'sm' && 'text-base')}>
                  {value}
                </p>
                {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
              </div>
            </div>
            {trend !== undefined && (
              <span className={cn('text-xs font-medium mt-1', trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500')}>
                {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                {trendLabel && ` ${trendLabel}`}
              </span>
            )}
          </div>
        ) : (
          /* Vertical layout (original): title + icon on top, value below */
          <>
            <div className="flex items-center justify-between">
              <span className={cn('text-xs font-medium text-gray-500 tracking-wide', size === 'sm' && 'text-[10px]')}>
                {title}
              </span>
              {Icon && (
                <Icon className={cn('h-5 w-5', iconColor, showPulse && 'animate-pulse')} />
              )}
            </div>
            <div className="mt-3">
              <span className={cn('text-3xl font-bold text-gray-900', size === 'sm' && 'text-xl')}>
                {value}
              </span>
            </div>
            {(subtitle || trend !== undefined) && (
              <div className="mt-2 flex items-center gap-2">
                {subtitle && <span className="text-sm text-gray-500">{subtitle}</span>}
                {trend !== undefined && (
                  <span className={cn('text-xs font-medium', trend > 0 && 'text-green-600', trend < 0 && 'text-red-600', trend === 0 && 'text-gray-500')}>
                    {trend > 0 ? '+' : ''}{trend}%
                    {trendLabel && ` ${trendLabel}`}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
